import React, { useEffect, useMemo, useState } from 'react';
import { fetchAdminAnalytics } from '../../api/admin';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';
import FormSelect from '../common/form/FormSelect';

import styles from './styles/AdminAnalyticsTab.module.css';

const MetricCard = ({ title, value, sub }) => (
    <div className={styles.metricCard}>
        <div className={styles.metricTitle}>{title}</div>
        <div className={styles.metricValue}>{value}</div>
        {sub !== undefined && (
            <div className={styles.metricSub}>{sub}</div>
        )}
    </div>
);

const formatPercent = v => {
    if (v === undefined || v === null) return '-';
    return `${(v * 100).toFixed(1)}%`;
};

const formatCurrency = v => {
    if (v === undefined || v === null) return '0.00';
    if (typeof v === 'number') return v.toFixed(2);
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const AdminAnalyticsTab = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);
        fetchAdminAnalytics(days)
            .then((res) => { if (mounted) setData(res); })
            .catch((e) => { if (mounted) setError(e?.message || 'Failed'); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [days]);

    const dailySeries = useMemo(() => data?.series?.daily || [], [data]);

    return (
        <div className={styles.container}>
            <div className={styles.headerRow}>
                <h2 className={styles.title}>Analytics</h2>
                <div className={styles.controls}>
                    <label className={styles.label}>Range:</label>
                    <FormSelect
                        value={days}
                        onChange={(v) => setDays(parseInt(v))}
                        options={[
                            { value: 7, label: '7 days' },
                            { value: 14, label: '14 days' },
                            { value: 30, label: '30 days' },
                            { value: 60, label: '60 days' },
                            { value: 90, label: '90 days' },
                        ]}
                        name="range"
                    />
                </div>
            </div>

            {loading && <div className={styles.info}>Loading…</div>}
            {error && <div className={styles.error}>{error}</div>}

            {data && (
                <>
                    <div className={styles.cardsGrid}>
                        <MetricCard title="Total Users" value={data.totals.users.total} sub={`New Today: ${data.totals.users.newToday}`} />
                        <MetricCard title="User Growth (vs prev period)" value={formatPercent(data.totals.users.growthMoM)} sub={`New in period: ${data.totals.users.newPeriod}`} />
                        <MetricCard title="DAU / WAU / MAU" value={`${data.totals.activeUsers.DAU} / ${data.totals.activeUsers.WAU} / ${data.totals.activeUsers.MAU}`} />
                        <MetricCard title="Chats" value={`Total: ${data.totals.chats.total}`} sub={`New Today: ${data.totals.chats.newToday} · Active Today: ${data.totals.chats.activeToday}`} />
                        <MetricCard title="Agent Chats" value={`Total: ${data.totals.agentChats.total}`} sub={`New Today: ${data.totals.agentChats.newToday} · Active Today: ${data.totals.agentChats.activeToday}`} />
                        {data.totals.paying && (
                            <MetricCard title="Paying Users & Revenue" value={`Paying: ${data.totals.paying.activePayingUsers}`} sub={`Revenue: $${formatCurrency(data.totals.paying.revenuePeriod)} · Rev Growth: ${formatPercent(data.totals.paying.revenueGrowth)}`} />
                        )}
                    </div>

                    <div className={styles.chartsGrid}>
                        <div className={styles.chartCard}>
                            <div className={styles.chartTitle}>New Users & Active Users (Daily)</div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dailySeries} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="newUsers" stroke="#1f77b4" name="New Users" dot={false} strokeWidth={2} />
                                    <Line type="monotone" dataKey="activeUsers" stroke="#2ca02c" name="Active Users" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={styles.chartCard}>
                            <div className={styles.chartTitle}>Chats (New vs Active by Day)</div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dailySeries} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="newChats" stroke="#9467bd" name="New Chats" dot={false} strokeWidth={2} />
                                    <Line type="monotone" dataKey="activeChats" stroke="#8c564b" name="Active Chats" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={styles.chartCard}>
                            <div className={styles.chartTitle}>Agent Chats (New vs Active by Day)</div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dailySeries} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="newAgentChats" stroke="#ff7f0e" name="New Agent Chats" dot={false} strokeWidth={2} />
                                    <Line type="monotone" dataKey="activeAgentChats" stroke="#d62728" name="Active Agent Chats" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <h3 className={styles.title}>Revenue & Subscriptions</h3>

                        <div className={styles.chartCard}>
                            <div className={styles.chartTitle}>Paying Subscriptions (Daily)</div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dailySeries} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="payingSubs" stroke="#1f77b4" name="New Paying Subs" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={styles.chartCard}>
                            <div className={styles.chartTitle}>Revenue (Daily, $)</div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dailySeries} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="revenue" stroke="#2ca02c" name="Revenue ($)" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminAnalyticsTab;
